import { connect, type Socket } from "node:net";
import { existsSync, mkdirSync, readdirSync } from "node:fs";
import { localIpcEndpointMayExist, removeLocalIpcEndpoint } from "../common/local-ipc-endpoint.js";
import type { ChildProcess } from "node:child_process";
import {
  buildMessage,
  serializeControl,
  SessionState,
  type ControlErrorCode as ControlErrorCodeType,
  type CommandEntry,
} from "@dev-anywhere/shared";
import { serviceLogger } from "../common/logger.js";
import {
  IGNORED_EVENT_TYPES,
  StreamJsonEventSchema,
  type StreamJsonEvent,
} from "../common/stream-json-schema.js";
import { DATA_DIR, sessionPaths } from "../common/paths.js";
import { spawnScript } from "../common/env.js";
import { isManagedSessionProcess } from "../common/managed-session-process.js";
import { getSeqCounterFor } from "../common/seq-counter.js";
import {
  WORKER_IPC_PROTOCOL_VERSION,
  createWorkerReader,
  serializeWorkerMsg,
  type WorkerMessage,
} from "../ipc/ipc-protocol.js";
import { mapClaudeStreamEvent } from "./claude-stream-event-mapper.js";
import { mapCodexAppServerEvent } from "./codex-app-server-event-mapper.js";
import { KimiAcpEventMapper } from "./kimi-acp-event-mapper.js";
import type { SessionManager } from "./session-manager.js";
import type { RelayConnection } from "./relay-connection.js";
import type { JsonObserver } from "./json-observer.js";
import type { ProviderHookContext, ProviderId } from "../providers/index.js";
import type { PermissionBroker, PermissionDecision } from "./permission-broker.js";

interface CompactCommandOutcome {
  success: boolean;
  message: string;
}

interface WorkerRegistryDeps {
  sessionManager: SessionManager;
  permissionBroker: PermissionBroker;
  relayConnection: RelayConnection;
  // JSON 观察通道状态机；forwardEvent / forwardApprovalRequest 据此推状态变迁
  jsonObserver: JsonObserver;
  touchSessionActivity?: (sessionId: string) => boolean;
  getProviderEnv: () => NodeJS.ProcessEnv;
  setProviderCommands?: (sessionId: string, commands: CommandEntry[]) => void;
  nextSeq?: (sessionId: string) => number;
  isManagedSessionProcess?: typeof isManagedSessionProcess;
  terminateManagedSession?: (pid: number) => void;
}

interface SpawnOptions {
  provider: ProviderId;
  cwd?: string;
  resumeSessionId?: string;
  permissionMode?: string;
  // 开启后 worker spawn claude 带 --include-partial-messages，forwardEvent 处理 stream_event delta；
  // aggregated assistant 的 text/thinking 会被跳过避免和 delta 重复
  streamDelta?: boolean;
  hook?: ProviderHookContext;
}

interface ReadyWaiter {
  resolve: () => void;
  reject: (err: Error) => void;
  timeout: NodeJS.Timeout;
}

interface NativeSessionRef {
  provider: ProviderId;
  sessionId: string;
}

interface AssistantSnapshotState {
  turnId: string;
  revision: number;
  text: string;
  status: "streaming" | "completed";
}

const RECONNECT_WORKER_HANDSHAKE_TIMEOUT_MS = 1_000;

export class WorkerStartupError extends Error {
  constructor(
    message: string,
    readonly errorCode?: ControlErrorCodeType,
    readonly nativeSessionId?: string,
  ) {
    super(message);
    this.name = "WorkerStartupError";
  }
}

const ANSI_ESCAPE_PATTERN = new RegExp(`${String.fromCharCode(27)}\\[[0-?]*[ -/]*[@-~]`, "g");

function normalizeLocalCommandText(content: string): string {
  return content
    .replace(ANSI_ESCAPE_PATTERN, "")
    .replace(/<\/?local-command-(?:stdout|stderr)>/g, "")
    .trim();
}

function parseCompactCommandOutcome(content: string): CompactCommandOutcome | null {
  const normalized = normalizeLocalCommandText(content);
  if (!normalized) return null;

  if (content.includes("<local-command-stderr>")) {
    let detail = normalized
      .replace(/^Error:\s*/i, "")
      .replace(/^Error during compaction:\s*/i, "")
      .trim();
    if (/No messages to compact/i.test(detail)) {
      return { success: true, message: "没有可压缩的上下文。" };
    }
    if (!detail) detail = "请稍后重试。";
    return { success: false, message: `上下文压缩失败：${detail}` };
  }

  if (content.includes("<local-command-stdout>") || /\bCompacted\b/i.test(normalized)) {
    return { success: true, message: "上下文压缩完成。" };
  }

  return null;
}

// 管理 session → worker socket 的映射，封装全部 worker IO：
// - spawn / connect / reconnectAll / destroyAll 生命周期入口
// - send(sessionId, msg) 统一出口
// - worker_event 路由、worker_approval_request 转发、worker_exit 清理都在内部闭环
export class WorkerRegistry {
  private sockets = new Map<string, Socket>();
  private children = new Map<string, ChildProcess>();
  private providers = new Map<string, ProviderId>();
  private readySessions = new Set<string>();
  private readyWaiters = new Map<string, Set<ReadyWaiter>>();
  private pendingNativeSessions = new Map<string, NativeSessionRef>();
  private assistantSnapshots = new Map<string, AssistantSnapshotState>();
  private readonly kimiAcpEventMapper = new KimiAcpEventMapper();
  private startupFailures = new Map<string, WorkerStartupError>();
  private assistantTurnCounter = 0;

  constructor(private deps: WorkerRegistryDeps) {
    // relay queue 溢出时，被 drop 的 envelope 不会到达 client；若是 tool_use_request，
    // 主动清 pending 审批并回 worker env-failure deny，避免 worker 永挂、permission broker 泄漏。
    deps.relayConnection.on("envelope_dropped", (raw: string) => this.onEnvelopeDropped(raw));
  }

  private onEnvelopeDropped(raw: string): void {
    let parsed: unknown;
    try {
      parsed = JSON.parse(raw);
    } catch {
      return;
    }
    if (
      !parsed ||
      typeof parsed !== "object" ||
      (parsed as { type?: unknown }).type !== "tool_use_request"
    ) {
      return;
    }
    const envelope = parsed as {
      sessionId?: unknown;
      payload?: { toolId?: unknown };
    };
    const sessionId = typeof envelope.sessionId === "string" ? envelope.sessionId : null;
    const requestId =
      envelope.payload && typeof envelope.payload.toolId === "string"
        ? envelope.payload.toolId
        : null;
    if (!sessionId || !requestId) return;
    if (
      !this.deps.permissionBroker.resolve(requestId, {
        behavior: "deny",
        message: "Approval request was dropped due to relay queue overflow.",
      })
    ) {
      return;
    }

    serviceLogger.warn(
      { sessionId, requestId },
      "Tool approval request lost to relay queue overflow, denying worker",
    );
  }

  waitForReady(sessionId: string, timeoutMs = 15_000): Promise<void> {
    const startupFailure = this.startupFailures.get(sessionId);
    if (startupFailure) return Promise.reject(startupFailure);
    if (this.readySessions.has(sessionId)) return Promise.resolve();
    return new Promise((resolve, reject) => {
      const timeout = setTimeout(() => {
        const waiters = this.readyWaiters.get(sessionId);
        if (waiters) {
          waiters.delete(waiter);
          if (waiters.size === 0) this.readyWaiters.delete(sessionId);
        }
        reject(new Error(`Worker did not report ready within ${timeoutMs}ms`));
      }, timeoutMs);
      timeout.unref?.();
      const waiter: ReadyWaiter = { resolve, reject, timeout };
      const waiters = this.readyWaiters.get(sessionId) ?? new Set<ReadyWaiter>();
      waiters.add(waiter);
      this.readyWaiters.set(sessionId, waiters);
    });
  }

  spawn(sessionId: string, options: SpawnOptions): number {
    const paths = sessionPaths(sessionId);
    // Recovery enumerates session directories even when IPC itself is a Windows named pipe.
    mkdirSync(paths.dir, { recursive: true });
    const args: string[] = [sessionId, paths.workerSock];
    args.push("--provider", options.provider);
    if (options.cwd) args.push("--cwd", options.cwd);
    if (options.resumeSessionId) args.push("--resume", options.resumeSessionId);
    // Claude 的远程安全默认值仍是 default；Codex 未显式选择时保留其本机配置。
    // Codex 的显式值已在 session_create 边界做过严格校验。
    const provider = options.provider;
    const permissionMode =
      options.permissionMode ?? (provider === "claude" ? "default" : undefined);
    if (permissionMode) args.push("--permission-mode", permissionMode);
    if (options.streamDelta) {
      args.push("--stream-delta");
    }
    if (options.hook) {
      args.push(
        "--hook-provider",
        options.hook.provider,
        "--hook-url",
        options.hook.hookUrl,
        "--hook-marker",
        options.hook.marker,
      );
    }
    args.push("--");

    const providerEnv = this.deps.getProviderEnv();
    const child = spawnScript("session-worker", args, {
      logger: serviceLogger,
      env: options.hook
        ? { ...providerEnv, DEV_ANYWHERE_HOOK_TOKEN: options.hook.token }
        : providerEnv,
    });
    const workerPid = child.pid!;
    this.children.set(sessionId, child);
    this.providers.set(sessionId, provider);
    // 正常退出会先通过 worker_exit IPC 删除 session；SIGKILL/OOM 等路径来不及发 IPC，
    // 只能依赖父进程的 child exit。两条路径都落到 SessionManager.onSessionRemoved，
    // 由统一出口清审批、临时目录并广播会话列表。
    child.once("exit", (code, signal) => {
      const activeSession = this.deps.sessionManager.getSession(sessionId);
      this.delete(sessionId);
      if (!activeSession) return;
      const result = this.deps.sessionManager.terminateSession(sessionId);
      serviceLogger.warn(
        { sessionId, workerPid, code, signal, removed: result.success },
        "JSON worker process exited without worker_exit IPC",
      );
    });
    serviceLogger.info(
      { sessionId, workerPid, cwd: options.cwd, resume: options.resumeSessionId },
      "Worker process spawned",
    );
    return workerPid;
  }

  connect(sessionId: string, sockPath: string, timeoutMs?: number): Promise<Socket | null> {
    return new Promise((resolve) => {
      const sock = connect(sockPath);
      let settled = false;
      let timeout: NodeJS.Timeout | undefined;
      const finish = (result: Socket | null): void => {
        if (settled) return;
        settled = true;
        if (timeout) clearTimeout(timeout);
        resolve(result);
      };
      const onConnectError = (): void => finish(null);
      sock.once("error", onConnectError);
      sock.once("connect", () => {
        sock.off("error", onConnectError);
        this.sockets.set(sessionId, sock);
        this.readySessions.delete(sessionId);
        let protocolAccepted = false;
        let workerReady = false;
        let startupFailed = false;
        let negotiatedProvider: ProviderId | null = null;
        createWorkerReader(
          sock,
          (msg) => {
            try {
              if (this.sockets.get(sessionId) !== sock) {
                sock.destroy();
                return;
              }
              if (!protocolAccepted) {
                if (msg.type !== "worker_protocol_hello") {
                  this.rejectForeignWorkerProtocol(sessionId, null, sock, msg.type);
                  return;
                }
                if (
                  msg.sessionId !== sessionId ||
                  !this.matchesExpectedWorkerIdentity(sessionId, msg.pid, msg.provider)
                ) {
                  this.rejectForeignWorkerProtocol(
                    sessionId,
                    msg.protocolVersion,
                    sock,
                    "worker_protocol_hello identity mismatch",
                  );
                  return;
                }
                protocolAccepted = true;
                negotiatedProvider = msg.provider;
                serviceLogger.debug(
                  { sessionId, workerPid: msg.pid, protocolVersion: msg.protocolVersion },
                  "Worker IPC protocol accepted",
                );
                return;
              }
              if (msg.type === "worker_protocol_hello") {
                this.rejectForeignWorkerProtocol(
                  sessionId,
                  msg.protocolVersion,
                  sock,
                  "duplicate worker_protocol_hello",
                );
                return;
              }
              if (
                msg.type === "serve_protocol_hello" ||
                msg.type === "worker_input" ||
                msg.type === "worker_stop" ||
                msg.type === "worker_interrupt" ||
                msg.type === "worker_approval_response" ||
                msg.type === "worker_whitelist_add"
              ) {
                this.rejectForeignWorkerProtocol(
                  sessionId,
                  WORKER_IPC_PROTOCOL_VERSION,
                  sock,
                  `invalid worker-to-serve message type: ${msg.type}`,
                );
                return;
              }
              if (
                (msg.type === "worker_startup_error" || msg.type === "worker_native_session_id") &&
                msg.provider !== negotiatedProvider
              ) {
                this.rejectForeignWorkerProtocol(
                  sessionId,
                  WORKER_IPC_PROTOCOL_VERSION,
                  sock,
                  `${msg.type} provider identity mismatch`,
                );
                return;
              }
              if (
                msg.type === "worker_ready" &&
                msg.nativeSession !== undefined &&
                msg.nativeSession.provider !== negotiatedProvider
              ) {
                this.rejectForeignWorkerProtocol(
                  sessionId,
                  WORKER_IPC_PROTOCOL_VERSION,
                  sock,
                  "worker_ready provider identity mismatch",
                );
                return;
              }
              if (!workerReady) {
                if (startupFailed && msg.type !== "worker_exit") {
                  this.rejectForeignWorkerProtocol(
                    sessionId,
                    WORKER_IPC_PROTOCOL_VERSION,
                    sock,
                    `${msg.type} arrived after worker startup failure`,
                  );
                  return;
                }
                if (
                  msg.type !== "worker_ready" &&
                  msg.type !== "worker_startup_error" &&
                  msg.type !== "worker_exit" &&
                  msg.type !== "worker_native_session_id"
                ) {
                  this.rejectForeignWorkerProtocol(
                    sessionId,
                    WORKER_IPC_PROTOCOL_VERSION,
                    sock,
                    `${msg.type} arrived before worker readiness`,
                  );
                  return;
                }
                if (msg.type === "worker_ready") workerReady = true;
                if (msg.type === "worker_startup_error") startupFailed = true;
              } else if (msg.type === "worker_ready" || msg.type === "worker_startup_error") {
                this.rejectForeignWorkerProtocol(
                  sessionId,
                  WORKER_IPC_PROTOCOL_VERSION,
                  sock,
                  `${msg.type} arrived after worker readiness`,
                );
                return;
              }
              this.handleWorkerMessage(sessionId, msg);
            } catch (err) {
              serviceLogger.error(
                { sessionId, error: err instanceof Error ? err.message : String(err) },
                "Worker message handler failed",
              );
            }
          },
          (err, line) => {
            if (this.sockets.get(sessionId) !== sock) {
              sock.destroy();
              return;
            }
            if (!protocolAccepted) {
              const invalidHelloVersion = this.readProtocolHelloVersion(line);
              this.rejectForeignWorkerProtocol(
                sessionId,
                invalidHelloVersion,
                sock,
                "invalid or missing worker_protocol_hello",
              );
              return;
            }
            // 单条 worker NDJSON 行 schema 校验失败：warn 而非断连。Claude/Codex CLI 增量
            // 加新事件类型时不该把整个 session 推进 ERROR；连接保持开放，下一条仍继续解析。
            serviceLogger.warn(
              {
                sessionId,
                err: err.message,
                lineLen: line.length,
              },
              "Worker IPC message dropped (parse/schema error)",
            );
          },
        );
        sock.on("close", () => this.onDisconnect(sessionId, sock));
        sock.on("error", () => this.onDisconnect(sessionId, sock));
        sock.write(
          serializeWorkerMsg({
            type: "serve_protocol_hello",
            protocolVersion: WORKER_IPC_PROTOCOL_VERSION,
            sessionId,
            pid: process.pid,
          }),
        );
        finish(sock);
      });
      if (timeoutMs !== undefined) {
        timeout = setTimeout(
          () => {
            sock.destroy();
            finish(null);
          },
          Math.max(1, timeoutMs),
        );
        timeout.unref?.();
      }
    });
  }

  // 枚举 DATA_DIR 下所有 session 目录，尝试连接存活的 worker.sock；失败则清理 stale socket。
  async reconnectAll(): Promise<void> {
    const dirs = existsSync(DATA_DIR)
      ? readdirSync(DATA_DIR, { withFileTypes: true }).filter((d) => d.isDirectory())
      : [];

    for (const dir of dirs) {
      const sessionId = dir.name;
      const paths = sessionPaths(sessionId);
      if (!localIpcEndpointMayExist(paths.workerSock)) continue;

      const persistedSession = this.deps.sessionManager.getSession(sessionId);
      if (!persistedSession || persistedSession.mode !== "json") {
        await this.terminateOrphanWorker(sessionId, paths.workerSock);
        continue;
      }

      const sock = await this.connect(sessionId, paths.workerSock);
      if (sock) {
        try {
          await this.waitForReady(sessionId, RECONNECT_WORKER_HANDSHAKE_TIMEOUT_MS);
        } catch {
          if (this.deps.sessionManager.getSession(sessionId)) {
            this.rejectForeignWorkerProtocol(sessionId);
          }
          continue;
        }
        serviceLogger.info({ sessionId }, "Reconnected to existing worker");
      } else {
        try {
          removeLocalIpcEndpoint(paths.workerSock);
        } catch {
          // socket 文件可能已被删除
        }
        serviceLogger.info({ sessionId }, "Cleaned up stale worker socket");
      }
    }

    // Persisted JSON sessions are usable only when their worker completed a current-generation
    // socket handshake. A missing directory/socket or failed connection must not leave a ghost
    // session in Relay/Web state.
    for (const session of this.deps.sessionManager.listSessions()) {
      if (session.mode === "json" && !this.has(session.id)) {
        this.discardUnconnectedJsonSession(session.id);
      }
    }
  }

  has(sessionId: string): boolean {
    return this.readySessions.has(sessionId) && this.sockets.has(sessionId);
  }

  hasProcess(sessionId: string): boolean {
    return this.children.has(sessionId);
  }

  getStartupFailure(sessionId: string): WorkerStartupError | undefined {
    return this.startupFailures.get(sessionId);
  }

  delete(sessionId: string): void {
    this.children.delete(sessionId);
    this.providers.delete(sessionId);
    this.sockets.delete(sessionId);
    this.readySessions.delete(sessionId);
    this.pendingNativeSessions.delete(sessionId);
    this.assistantSnapshots.delete(sessionId);
    this.kimiAcpEventMapper.clearSession(sessionId);
    this.rejectReadyWaiters(
      new Error(`Worker session deleted before ready: ${sessionId}`),
      sessionId,
    );
  }

  terminateProcess(sessionId: string, signal: NodeJS.Signals = "SIGTERM"): boolean {
    const child = this.children.get(sessionId);
    const sock = this.sockets.get(sessionId);
    sock?.destroy();
    this.sockets.delete(sessionId);
    this.readySessions.delete(sessionId);
    this.pendingNativeSessions.delete(sessionId);
    this.assistantSnapshots.delete(sessionId);
    this.kimiAcpEventMapper.clearSession(sessionId);
    this.startupFailures.delete(sessionId);
    this.children.delete(sessionId);
    this.providers.delete(sessionId);
    this.rejectReadyWaiters(
      new Error(`Worker process terminated before ready: ${sessionId}`),
      sessionId,
    );
    if (!child || child.killed) return false;
    return child.kill(signal);
  }

  // 向指定 session 的 worker 写 WorkerMessage；socket 缺失或不可写返回 false 由 caller 决定日志。
  send(sessionId: string, msg: WorkerMessage): boolean {
    const sock = this.sockets.get(sessionId);
    if (!this.readySessions.has(sessionId) || !sock?.writable) return false;
    sock.write(serializeWorkerMsg(msg));
    return true;
  }

  destroyAll(): void {
    for (const [, ws] of this.sockets) {
      ws.destroy();
    }
    this.sockets.clear();
    this.providers.clear();
    this.readySessions.clear();
    this.pendingNativeSessions.clear();
    this.assistantSnapshots.clear();
    this.kimiAcpEventMapper.clear();
    this.startupFailures.clear();
    this.rejectReadyWaiters(new Error("Worker registry destroyed"));
  }

  private handleWorkerMessage(sessionId: string, msg: WorkerMessage): void {
    switch (msg.type) {
      case "worker_ready":
        this.startupFailures.delete(sessionId);
        if (msg.nativeSession) {
          this.captureNativeSession(sessionId, msg.nativeSession);
        }
        this.readySessions.add(sessionId);
        this.resolveReadyWaiters(sessionId);
        serviceLogger.info({ sessionId, pid: msg.pid }, "Worker ready");
        break;

      case "worker_startup_error": {
        const startupError = new WorkerStartupError(
          msg.message,
          msg.errorCode,
          msg.nativeSessionId,
        );
        this.startupFailures.set(sessionId, startupError);
        this.rejectReadyWaiters(startupError, sessionId);
        serviceLogger.warn(
          { sessionId, provider: msg.provider, errorCode: msg.errorCode, errorTail: msg.message },
          "JSON worker provider failed before ready",
        );
        break;
      }

      case "worker_event":
        try {
          this.forwardEvent(sessionId, msg.seq, msg.event);
        } catch (err) {
          serviceLogger.debug(
            { sessionId, error: String(err) },
            "Failed to forward event to relay",
          );
        }
        serviceLogger.debug({ sessionId, eventType: msg.event.type }, "JSON session event");
        break;

      case "worker_exit":
        this.deps.sessionManager.terminateSession(sessionId);
        this.delete(sessionId);
        serviceLogger[msg.code === 0 ? "info" : "warn"](
          {
            sessionId,
            exitCode: msg.code,
            ...(msg.errorTail ? { errorTail: msg.errorTail } : {}),
          },
          "JSON session exited",
        );
        break;

      case "worker_interrupted":
        this.kimiAcpEventMapper.finishTurn(sessionId);
        this.deps.permissionBroker.cleanupSession(sessionId, "Turn interrupted");
        this.deps.relayConnection.sendRaw(
          serializeControl({
            type: "pending_approvals_push",
            sessionId,
            approvals: [],
          }),
        );
        this.deps.relayConnection.sendRaw(
          serializeControl({
            type: "turn_result",
            sessionId,
            success: false,
            isError: true,
          }),
        );
        this.deps.jsonObserver.onTurnResult(sessionId);
        serviceLogger.info({ sessionId }, "JSON turn interrupted");
        break;

      case "worker_turn_started":
        this.deps.jsonObserver.onTurnStart(sessionId);
        serviceLogger.debug({ sessionId }, "Queued JSON turn started");
        break;

      case "worker_approval_request":
        this.forwardApprovalRequest(sessionId, msg);
        break;

      case "worker_native_session_id":
        this.captureNativeSession(sessionId, {
          provider: msg.provider,
          sessionId: msg.sessionId,
        });
        serviceLogger.info(
          { sessionId, provider: msg.provider, nativeSessionId: msg.sessionId },
          "Native JSON session ID captured",
        );
        break;

      case "worker_protocol_hello":
        // Socket-level negotiation is handled before messages enter this switch.
        break;
    }
  }

  private readProtocolHelloVersion(line: string): string | number | null {
    try {
      const value = JSON.parse(line) as { type?: unknown; protocolVersion?: unknown };
      if (value?.type !== "worker_protocol_hello") return null;
      return typeof value.protocolVersion === "string" || typeof value.protocolVersion === "number"
        ? value.protocolVersion
        : null;
    } catch {
      return null;
    }
  }

  private terminateOrphanWorker(sessionId: string, workerSocketPath: string): Promise<void> {
    return new Promise((resolve) => {
      const socket = connect(workerSocketPath);
      let settled = false;
      const finish = (details: {
        pid?: number;
        provider?: ProviderId;
        terminated: boolean;
      }): void => {
        if (settled) return;
        settled = true;
        clearTimeout(timeout);
        socket.destroy();
        try {
          removeLocalIpcEndpoint(workerSocketPath);
        } catch {
          // The worker may already have removed its socket while handling SIGTERM.
        }
        serviceLogger.warn(
          { sessionId, ...details },
          "Orphaned JSON worker removed without session data",
        );
        resolve();
      };
      const timeout = setTimeout(
        () => finish({ terminated: false }),
        RECONNECT_WORKER_HANDSHAKE_TIMEOUT_MS,
      );
      timeout.unref?.();

      socket.once("error", () => finish({ terminated: false }));
      socket.once("connect", () => {
        createWorkerReader(
          socket,
          (msg) => {
            if (msg.type !== "worker_protocol_hello" || msg.sessionId !== sessionId) {
              finish({ terminated: false });
              return;
            }
            const identity = {
              id: sessionId,
              mode: "json" as const,
              provider: msg.provider,
              workerSocketPath,
            };
            const inspect = this.deps.isManagedSessionProcess ?? isManagedSessionProcess;
            if (!inspect(msg.pid, identity)) {
              finish({ pid: msg.pid, provider: msg.provider, terminated: false });
              return;
            }
            let terminated = false;
            try {
              (this.deps.terminateManagedSession ?? ((pid) => process.kill(pid, "SIGTERM")))(
                msg.pid,
              );
              terminated = true;
            } catch {
              // The verified process may have exited before signaling.
            }
            finish({ pid: msg.pid, provider: msg.provider, terminated });
          },
          () => finish({ terminated: false }),
        );
        socket.write(
          serializeWorkerMsg({
            type: "serve_protocol_hello",
            protocolVersion: WORKER_IPC_PROTOCOL_VERSION,
            sessionId,
            pid: process.pid,
          }),
        );
      });
    });
  }

  private matchesExpectedWorkerIdentity(
    sessionId: string,
    receivedPid: number,
    receivedProvider: ProviderId,
  ): boolean {
    const childPid = this.children.get(sessionId)?.pid;
    if (childPid !== undefined) {
      return childPid === receivedPid && this.providers.get(sessionId) === receivedProvider;
    }
    const persisted = this.deps.sessionManager.getSession(sessionId);
    return (
      persisted?.mode === "json" &&
      persisted.pid === receivedPid &&
      persisted.provider === receivedProvider
    );
  }

  private discardUnconnectedJsonSession(sessionId: string): void {
    const session = this.deps.sessionManager.getSession(sessionId);
    if (!session || session.mode !== "json") return;
    const child = this.children.get(sessionId);
    const result = this.deps.sessionManager.terminateSession(sessionId);
    const pid = result.pid ?? session.pid;
    this.delete(sessionId);
    this.startupFailures.delete(sessionId);

    let terminated = false;
    if (child && !child.killed) {
      terminated = child.kill("SIGTERM");
    } else {
      if (
        pid &&
        isManagedSessionProcess(pid, {
          id: sessionId,
          mode: "json",
          provider: session.provider,
          workerSocketPath: sessionPaths(sessionId).workerSock,
        })
      ) {
        try {
          process.kill(pid, "SIGTERM");
          terminated = true;
        } catch {
          // The process may have exited between identity verification and signaling.
        }
      }
    }

    serviceLogger.warn(
      { sessionId, pid, terminated },
      "Removed JSON session without a connected current-generation worker",
    );
  }

  private rejectForeignWorkerProtocol(
    sessionId: string,
    receivedProtocolVersion: string | number | null = null,
    workerSocket?: Socket,
    reason?: string,
  ): void {
    const socket = workerSocket ?? this.sockets.get(sessionId);
    if (workerSocket && this.sockets.get(sessionId) !== workerSocket) {
      workerSocket.destroy();
      return;
    }
    const child = this.children.get(sessionId);
    const session = this.deps.sessionManager.getSession(sessionId);
    const error = new WorkerStartupError("Worker IPC protocol handshake rejected");

    this.sockets.delete(sessionId);
    this.children.delete(sessionId);
    this.providers.delete(sessionId);
    this.readySessions.delete(sessionId);
    this.pendingNativeSessions.delete(sessionId);
    this.assistantSnapshots.delete(sessionId);
    this.kimiAcpEventMapper.clearSession(sessionId);
    if (child) this.startupFailures.set(sessionId, error);
    else this.startupFailures.delete(sessionId);
    this.rejectReadyWaiters(error, sessionId);

    const result = this.deps.sessionManager.terminateSession(sessionId);
    socket?.destroy();

    let terminated = false;
    if (child && !child.killed) {
      terminated = child.kill("SIGTERM");
    } else {
      const pid = result.pid;
      const workerSocketPath = sessionPaths(sessionId).workerSock;
      if (
        pid &&
        session?.mode === "json" &&
        isManagedSessionProcess(pid, {
          id: sessionId,
          mode: "json",
          provider: session.provider,
          workerSocketPath,
        })
      ) {
        try {
          process.kill(pid, "SIGTERM");
          terminated = true;
        } catch {
          // 进程已退出时会落到这里，session/socket 仍然已完成清理。
        }
      }
    }

    serviceLogger.warn(
      {
        sessionId,
        expectedProtocolVersion: WORKER_IPC_PROTOCOL_VERSION,
        receivedProtocolVersion,
        reason,
        terminated,
      },
      "Rejected JSON worker from a foreign IPC generation",
    );
  }

  takePendingNativeSession(sessionId: string): NativeSessionRef | undefined {
    const nativeSession = this.pendingNativeSessions.get(sessionId);
    this.pendingNativeSessions.delete(sessionId);
    return nativeSession;
  }

  private captureNativeSession(sessionId: string, nativeSession: NativeSessionRef): void {
    if (!this.deps.sessionManager.getSession(sessionId)) {
      this.pendingNativeSessions.set(sessionId, nativeSession);
      return;
    }
    if (nativeSession.provider === "claude") {
      this.deps.sessionManager.setClaudeSessionId(sessionId, nativeSession.sessionId);
    } else {
      this.deps.sessionManager.setHistorySessionId(sessionId, nativeSession.sessionId);
    }
  }

  // worker 连接断开或异常时的统一清理入口。仅记录一份，不再区分 close vs error 语义。
  private onDisconnect(sessionId: string, socket: Socket): void {
    if (this.sockets.get(sessionId) !== socket) return;
    this.sockets.delete(sessionId);
    this.readySessions.delete(sessionId);
    this.rejectReadyWaiters(new Error(`Worker disconnected before ready: ${sessionId}`), sessionId);
    this.deps.permissionBroker.cleanupSession(sessionId, "Worker disconnected");
    // worker_exit 消息走 handleWorkerMessage，那条路径已经 terminateSession 把 session 从 manager
    // 中删掉——onDisconnect 紧随其后到达时 getSession 返回 undefined，不触发 ERROR 转换。
    // 只有"未经 worker_exit 即断连"（进程崩溃 / 内核 OOM kill / IPC socket 损坏）才会进入这里：
    // session 仍然在 manager 中，必须立即把状态推到 ERROR，否则 UI 看到的状态会停留在
    // WORKING / WAITING_APPROVAL 直到 reaper 60s 周期触发，期间用户既无法手动中止也无法重启。
    if (this.deps.sessionManager.getSession(sessionId)) {
      this.deps.jsonObserver.onChannelBroken(sessionId);
    }
  }

  private resolveReadyWaiters(sessionId: string): void {
    const waiters = this.readyWaiters.get(sessionId);
    if (!waiters) return;
    this.readyWaiters.delete(sessionId);
    for (const waiter of waiters) {
      clearTimeout(waiter.timeout);
      waiter.resolve();
    }
  }

  private rejectReadyWaiters(err: Error, sessionId?: string): void {
    const entries =
      sessionId === undefined
        ? [...this.readyWaiters.entries()]
        : ([[sessionId, this.readyWaiters.get(sessionId)]] as Array<
            [string, Set<ReadyWaiter> | undefined]
          >);
    for (const [sid, waiters] of entries) {
      if (!waiters) continue;
      this.readyWaiters.delete(sid);
      for (const waiter of waiters) {
        clearTimeout(waiter.timeout);
        waiter.reject(err);
      }
    }
  }

  // 对齐 Claude CLI stream-json 输出，按 type 分发：
  //   stream_event.content_block_delta → 增量 text/thinking envelope（仅 streamDelta 会话产生）
  //   assistant.content[].text      → assistant_message envelope（streamDelta 下跳过，避免重复）
  //   assistant.content[].thinking  → thinking envelope（streamDelta 下跳过）
  //   assistant.content[].tool_use  → assistant_tool_use envelope
  //   user.content[].tool_result    → tool_result envelope
  //   system/local_command          → /compact 成功/失败结果 + 会话状态回 IDLE
  //   result                        → turn_result control + 会话状态回 IDLE
  //   rate_limit_event/其他         → 静默忽略
  // schema 未识别的 event/block 以 warn 暴露，作为 Claude CLI 协议变化的 runtime canary。
  private forwardEvent(sessionId: string, seq: number, event: Record<string, unknown>): void {
    const relay = this.deps.relayConnection;
    if (event.type === "kimi_acp") {
      this.forwardKimiAcpEvent(sessionId, seq, event);
      return;
    }
    if (event.type === "codex_app_server") {
      this.forwardCodexAppServerEvent(sessionId, seq, event);
      return;
    }
    const parsed = StreamJsonEventSchema.safeParse(event);
    if (!parsed.success) {
      const rawType = typeof event.type === "string" ? event.type : "<missing>";
      if (IGNORED_EVENT_TYPES.has(rawType)) {
        serviceLogger.debug({ sessionId, type: rawType }, "Dropped ignored stream-json event");
        return;
      }
      serviceLogger.warn(
        { sessionId, type: rawType, issues: parsed.error.issues.slice(0, 3) },
        "Unknown stream-json event type; Claude CLI schema may have changed",
      );
      return;
    }
    const ev = parsed.data;
    this.deps.touchSessionActivity?.(sessionId);
    // Claude worker 始终以 --stream-delta 启动；aggregated assistant text 会与
    // delta 重复，因此只消费增量内容。
    const isStreamDeltaSession = true;

    if (ev.type === "system") {
      if (this.handleCompactSystemEvent(sessionId, seq, ev)) return;
      serviceLogger.debug(
        { sessionId, subtype: ev.subtype, status: ev.status },
        "Dropped ignored stream-json system event",
      );
      return;
    }

    if (ev.type === "user") {
      if (typeof ev.message.content === "string") {
        this.handleCompactLocalCommandOutput(sessionId, seq, ev.message.content);
        return;
      }
    }

    for (const mapped of mapClaudeStreamEvent(sessionId, seq, {
      event: ev,
      isStreamDeltaSession,
      isCompactingSession: this.isCompactingSession(sessionId),
    })) {
      if (mapped.kind === "assistant_text") {
        this.appendAssistantSnapshot(sessionId, seq, mapped.text, mapped.turnId);
      } else if (mapped.kind === "envelope") {
        if (mapped.envelope.type === "assistant_tool_use") {
          this.completeAssistantSnapshot(sessionId, seq);
        }
        relay.sendEnvelope(mapped.envelope);
      } else if (mapped.kind === "control") {
        this.completeAssistantSnapshot(sessionId, seq);
        relay.sendRaw(mapped.raw);
        if (mapped.notifyTurnResult) this.deps.jsonObserver.onTurnResult(sessionId);
      } else {
        serviceLogger.warn(
          { sessionId, seq, blockType: mapped.blockType },
          "Unknown assistant content block; Claude CLI schema may have changed",
        );
      }
    }
  }

  private forwardCodexAppServerEvent(
    sessionId: string,
    seq: number,
    event: Record<string, unknown>,
  ): void {
    const relay = this.deps.relayConnection;
    this.deps.touchSessionActivity?.(sessionId);
    for (const mapped of mapCodexAppServerEvent(sessionId, seq, event)) {
      if (mapped.kind === "assistant_text") {
        this.appendAssistantSnapshot(sessionId, seq, mapped.text, mapped.turnId);
      } else if (mapped.kind === "envelope") {
        if (mapped.envelope.type === "assistant_tool_use") {
          this.completeAssistantSnapshot(sessionId, seq);
        }
        relay.sendEnvelope(mapped.envelope);
      } else {
        this.completeAssistantSnapshot(sessionId, seq);
        relay.sendRaw(mapped.raw);
        if (mapped.notifyTurnResult) this.deps.jsonObserver.onTurnResult(sessionId);
      }
    }
  }

  private forwardKimiAcpEvent(
    sessionId: string,
    seq: number,
    event: Record<string, unknown>,
  ): void {
    const relay = this.deps.relayConnection;
    this.deps.touchSessionActivity?.(sessionId);
    for (const mapped of this.kimiAcpEventMapper.map(sessionId, seq, event)) {
      if (mapped.kind === "assistant_text") {
        this.appendAssistantSnapshot(sessionId, seq, mapped.text);
      } else if (mapped.kind === "envelope") {
        if (
          mapped.envelope.type === "assistant_tool_use" ||
          mapped.envelope.type === "user_input"
        ) {
          this.completeAssistantSnapshot(sessionId, seq);
        }
        relay.sendEnvelope(mapped.envelope);
      } else if (mapped.kind === "control") {
        if (mapped.providerCommands) {
          this.deps.setProviderCommands?.(sessionId, mapped.providerCommands);
        }
        if (mapped.completeAssistant !== false) this.completeAssistantSnapshot(sessionId, seq);
        relay.sendRaw(mapped.raw);
        if (mapped.notifyTurnResult) this.deps.jsonObserver.onTurnResult(sessionId);
      } else {
        serviceLogger.warn(
          { sessionId, seq, updateType: mapped.updateType },
          "Unknown Kimi ACP session update; protocol may have changed",
        );
      }
    }
  }

  private nextAssistantTurnId(sessionId: string): string {
    this.assistantTurnCounter += 1;
    return `${sessionId}-assistant-${Date.now()}-${this.assistantTurnCounter}`;
  }

  private appendAssistantSnapshot(
    sessionId: string,
    seq: number,
    delta: string,
    sourceTurnId?: string,
  ): void {
    if (!delta) return;
    const previous = this.assistantSnapshots.get(sessionId);
    const sameTurn =
      previous?.status === "streaming" && (!sourceTurnId || previous.turnId === sourceTurnId);
    if (previous?.status === "streaming" && !sameTurn) {
      this.completeAssistantSnapshot(sessionId, seq);
    }
    const next: AssistantSnapshotState = sameTurn
      ? { ...previous, revision: previous.revision + 1, text: previous.text + delta }
      : {
          turnId: sourceTurnId ?? this.nextAssistantTurnId(sessionId),
          revision: 1,
          text: delta,
          status: "streaming",
        };
    this.assistantSnapshots.set(sessionId, next);
    this.sendAssistantSnapshot(sessionId, seq, next);
  }

  private completeAssistantSnapshot(sessionId: string, seq: number): void {
    const previous = this.assistantSnapshots.get(sessionId);
    if (!previous || previous.status === "completed") return;
    const completed: AssistantSnapshotState = {
      ...previous,
      revision: previous.revision + 1,
      status: "completed",
    };
    this.assistantSnapshots.set(sessionId, completed);
    this.sendAssistantSnapshot(sessionId, seq, completed);
  }

  replayAssistantSnapshot(sessionId: string): void {
    const snapshot = this.assistantSnapshots.get(sessionId);
    if (!snapshot || snapshot.status !== "streaming") return;
    const seq = this.deps.nextSeq?.(sessionId) ?? getSeqCounterFor(sessionId).next();
    this.sendAssistantSnapshot(sessionId, seq, snapshot);
  }

  private sendAssistantSnapshot(
    sessionId: string,
    seq: number,
    snapshot: AssistantSnapshotState,
  ): void {
    this.deps.relayConnection.sendEnvelope(
      buildMessage("assistant_message", sessionId, seq, snapshot, "proxy"),
    );
  }

  private handleCompactSystemEvent(
    sessionId: string,
    seq: number,
    ev: Extract<StreamJsonEvent, { type: "system" }>,
  ): boolean {
    if (!this.isCompactingSession(sessionId)) return false;
    if (ev.subtype === "status" && ev.status === "compacting") return true;
    if (ev.subtype !== "local_command" || typeof ev.content !== "string") return false;
    return this.handleCompactLocalCommandOutput(sessionId, seq, ev.content);
  }

  private handleCompactLocalCommandOutput(
    sessionId: string,
    seq: number,
    content: string,
  ): boolean {
    if (!this.isCompactingSession(sessionId)) return false;
    const outcome = parseCompactCommandOutcome(content);
    if (!outcome) return false;
    this.deps.relayConnection.sendEnvelope(
      buildMessage(
        "assistant_message",
        sessionId,
        seq,
        {
          turnId: this.nextAssistantTurnId(sessionId),
          revision: 1,
          text: outcome.message,
          status: "completed",
        },
        "proxy",
      ),
    );
    this.sendCompactTurnResult(sessionId, outcome.success, outcome.message);
    serviceLogger[outcome.success ? "info" : "warn"](
      { sessionId, success: outcome.success },
      "Compact command completed",
    );
    return true;
  }

  private sendCompactTurnResult(sessionId: string, success: boolean, result?: string): void {
    this.deps.relayConnection.sendRaw(
      serializeControl({
        type: "turn_result",
        sessionId,
        success,
        isError: !success,
        ...(result ? { result } : {}),
      }),
    );
    this.deps.jsonObserver.onTurnResult(sessionId);
  }

  private isCompactingSession(sessionId: string): boolean {
    return this.deps.sessionManager.getSession(sessionId)?.state === SessionState.COMPACTING;
  }

  private forwardApprovalRequest(
    sessionId: string,
    msg: Extract<WorkerMessage, { type: "worker_approval_request" }>,
  ): void {
    serviceLogger.info(
      { sessionId, toolName: msg.toolName, requestId: msg.requestId },
      "Tool approval forwarding to relay",
    );
    this.deps.jsonObserver.onApprovalRequested(sessionId);
    try {
      const approvalSeq = this.deps.nextSeq?.(sessionId) ?? getSeqCounterFor(sessionId).next();
      const envelope = buildMessage(
        "tool_use_request",
        sessionId,
        approvalSeq,
        {
          toolName: msg.toolName,
          toolId: msg.requestId,
          parameters: msg.input,
          ...(msg.options ? { options: msg.options } : {}),
        },
        "proxy",
      );
      const session = this.deps.sessionManager.getSession(sessionId);
      if (!session) {
        serviceLogger.warn(
          { sessionId, requestId: msg.requestId },
          "Rejecting approval request for missing JSON session",
        );
        this.send(sessionId, {
          type: "worker_approval_response",
          requestId: msg.requestId,
          behavior: "deny",
          message: "Session no longer exists.",
        });
        return;
      }
      const registered = this.deps.permissionBroker.registerWorkerRequest(
        {
          requestId: msg.requestId,
          provider: session.provider,
          sessionId,
          toolName: msg.toolName,
          input: msg.input,
          ...(msg.options ? { options: msg.options } : {}),
        },
        (decision: PermissionDecision) => {
          this.send(sessionId, {
            type: "worker_approval_response",
            requestId: msg.requestId,
            behavior: decision.behavior,
            ...(decision.message ? { message: decision.message } : {}),
            ...(decision.remember ? { remember: true } : {}),
            ...(decision.optionId ? { optionId: decision.optionId } : {}),
          });
        },
      );
      if (!registered) return;
      this.deps.relayConnection.sendEnvelope(envelope);
    } catch (err) {
      const resolved = this.deps.permissionBroker.resolve(msg.requestId, {
        behavior: "deny",
        message: "Failed to forward approval request to relay.",
      });
      if (!resolved) {
        this.send(sessionId, {
          type: "worker_approval_response",
          requestId: msg.requestId,
          behavior: "deny",
          message: "Failed to forward approval request to relay.",
        });
      }
      // envelope 构造失败回 deny，避免 worker 无限等待。
      serviceLogger.warn(
        { sessionId, error: String(err) },
        "Failed to forward tool approval to relay, denying",
      );
    }
  }
}
